import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Heart, MessageCircle, Share2, BadgeCheck, PenLine, Compass, Users, Sparkles, Trophy, Camera, BookOpen, ImagePlus } from "lucide-react";
import { API, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Seo from "../components/Seo";
import { organizationLd } from "../lib/seo";

const HERO = "https://images.unsplash.com/photo-1680926407811-b691b7c55b99?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

const WHO = [
  ["A Student", "🎓"], ["A Creator", "🎨"], ["A Beauty Enthusiast", "💄"], ["A Fashion Lover", "👗"],
  ["A Homemaker", "🏡"], ["A Professional", "💼"], ["A Small Business Owner", "🛍️"], ["An Aspiring Entrepreneur", "🚀"],
  ["A Content Creator", "📱"], ["Someone With a Story", "💬"],
];

const CAN_DO = [
  [PenLine, "Share", "Share your thoughts, experiences, ideas and stories."],
  [Compass, "Discover", "Discover people, trends, opportunities and inspiring stories."],
  [Trophy, "Participate", "Take part in Suvikka campaigns, challenges and activities."],
  [Sparkles, "Showcase", "Show your talent, creativity, style, products or ideas."],
  [Users, "Connect", "Meet women with similar interests and aspirations."],
  [BookOpen, "Grow", "Learn, collaborate and discover new possibilities."],
];

const PARTICIPATE = ["Share Your Story", "Join a Challenge", "Participate in Campaigns", "Submit Your Talent", "Feature Your Style", "Recommend a Product", "Become a Community Contributor"];

function FeedComposer({ onPosted }) {
  const { user } = useAuth();
  const fileRef = useRef();
  const [caption, setCaption] = useState("");
  const [media, setMedia] = useState([]);
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await API.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); setMedia([`${process.env.REACT_APP_BACKEND_URL}${data.url}`]); toast.success("Photo added"); }
    catch (err) { toast.error(errMsg(err)); }
  };
  const submit = async () => {
    if (!user) { toast.error("Log in to post, Sakhi 💗"); return; }
    if (!caption.trim()) { toast.error("Write something first"); return; }
    setBusy(true);
    try { const { data } = await API.post("/community/posts", { type: "photo", caption, media }); toast.success(data.message); setCaption(""); setMedia([]); onPosted?.(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="bg-white border border-stone-200 rounded-2xl p-4" data-testid="sakhi-composer">
      <p className="text-sm font-semibold text-stone-800 mb-2">What's on your mind, Sakhi?</p>
      <textarea value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="Write something…" rows={2} className="w-full border border-stone-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#6A1B29]" data-testid="sakhi-composer-input" />
      <div className="flex items-center gap-3 mt-2">
        <input ref={fileRef} type="file" accept="image/*" onChange={upload} className="hidden" />
        <button onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-1.5 text-sm text-stone-500 hover:text-[#6A1B29]" data-testid="sakhi-composer-photo"><ImagePlus className="w-4 h-4" /> Photo</button>
        {media[0] && <img src={media[0]} alt="" className="w-9 h-9 rounded object-cover" />}
        <button onClick={submit} disabled={busy} className="ml-auto bg-[#6A1B29] hover:bg-[#501325] text-white text-sm font-semibold px-5 py-2 rounded-full disabled:opacity-60" data-testid="sakhi-composer-post">{busy ? "Posting…" : "Post"}</button>
      </div>
    </div>
  );
}

export default function SakhiLanding() {
  const [posts, setPosts] = useState([]);
  const [stories, setStories] = useState([]);
  const [challenges, setChallenges] = useState([]);
  const [featured, setFeatured] = useState([]);
  const load = () => API.get("/community/posts").then((r) => setPosts(r.data.slice(0, 4))).catch(() => {});
  useEffect(() => {
    load();
    API.get("/sakhi/stories").then((r) => setStories(r.data)).catch(() => {});
    API.get("/sakhi/challenges").then((r) => setChallenges(r.data)).catch(() => {});
    API.get("/sakhi/featured").then((r) => setFeatured(r.data)).catch(() => {});
  }, []);
  return (
    <div className="bg-[#FAF6F1]" data-testid="sakhi-page">
      <Seo title="Suvikka Sakhi | Her Community. Her Voice. Her Opportunities." description="Suvikka Sakhi is a community for women to share their stories, discover opportunities, showcase their talent, participate in campaigns and be part of something meaningful." canonicalPath="/suvikka-sakhi" image={HERO} jsonLd={organizationLd} />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <img src={HERO} alt="A community of confident young Indian women together" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#2A1418]/90 via-[#2A1418]/70 to-[#2A1418]/40" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32 text-white">
          <p className="text-xs font-semibold tracking-[0.35em] uppercase text-[#F6A5BE]">Suvikka Sakhi</p>
          <h1 className="font-display text-4xl sm:text-6xl font-semibold mt-4 max-w-3xl leading-[1.08]">Where Women Connect, Create &amp; Grow Together.</h1>
          <p className="text-stone-200 mt-5 max-w-xl text-lg">Suvikka Sakhi is a community for women to share their stories, discover opportunities, showcase their talent, participate in campaigns and be part of something meaningful.</p>
          <div className="flex flex-wrap gap-3 mt-8">
            <Link to="/signup" data-testid="sakhi-join-cta" className="bg-[#6A1B29] hover:bg-[#501325] text-white font-semibold px-8 py-3.5 rounded-full transition-transform hover:scale-105">Join Suvikka Sakhi</Link>
            <a href="#feed" data-testid="sakhi-explore-cta" className="border border-white/50 hover:bg-white hover:text-stone-900 text-white font-semibold px-8 py-3.5 rounded-full transition-colors">Explore the Community</a>
          </div>
        </div>
      </section>

      {/* WHO IS A SAKHI */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        <h2 className="font-display text-3xl sm:text-4xl font-semibold text-stone-900 text-center">Who Is a Suvikka Sakhi?</h2>
        <p className="text-stone-500 text-center mt-3 max-w-xl mx-auto">She is every woman with a voice, a talent, a dream — or simply looking for a community of like-minded women.</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mt-10">
          {WHO.map(([label, emoji], i) => (
            <div key={i} className="bg-white border border-stone-200 rounded-2xl p-5 text-center hover:border-[#6A1B29] hover:-translate-y-0.5 transition-all" data-testid={`who-card-${i}`}>
              <span className="text-3xl">{emoji}</span>
              <p className="text-sm font-medium text-stone-800 mt-2">{label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* WHAT CAN A SAKHI DO */}
      <section className="bg-white border-y border-stone-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <h2 className="font-display text-3xl sm:text-4xl font-semibold text-stone-900 text-center">What Can a Sakhi Do?</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
            {CAN_DO.map(([Icon, t, d], i) => (
              <div key={i} className="rounded-2xl border border-stone-200 p-6 hover:shadow-lg transition-shadow" data-testid={`cando-${i}`}>
                <span className="inline-flex p-3 rounded-2xl bg-[#FFF0F4] text-[#6A1B29]"><Icon className="w-6 h-6" /></span>
                <h3 className="font-display text-xl font-semibold text-stone-900 mt-4">{t}</h3>
                <p className="text-sm text-stone-500 mt-2 leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COMMUNITY FEED PREVIEW */}
      <section id="feed" className="max-w-2xl mx-auto px-4 sm:px-6 py-16">
        <h2 className="font-display text-3xl font-semibold text-stone-900 mb-2">The Community Feed</h2>
        <p className="text-stone-500 mb-6">Real finds. Real experiences. Real girls.</p>
        <FeedComposer onPosted={load} />
        <div className="space-y-4 mt-6">
          {posts.map((p) => (
            <div key={p.id} className="bg-white border border-stone-200 rounded-2xl overflow-hidden" data-testid={`sakhi-feed-${p.id}`}>
              <div className="flex items-center gap-3 p-4">
                <span className="w-9 h-9 rounded-full bg-[#6A1B29] text-white grid place-items-center font-semibold">{p.user_name?.[0] || "S"}</span>
                <div><p className="text-sm font-semibold text-stone-900 flex items-center gap-1">{p.user_name}{p.is_club && <BadgeCheck className="w-4 h-4 text-[#6A1B29]" />}</p><p className="text-xs text-stone-400 capitalize">Sakhi · {p.type}</p></div>
              </div>
              {p.media?.[0] && <img src={p.media[0]} alt="" className="w-full aspect-video object-cover" />}
              <div className="p-4"><p className="text-sm text-stone-700">{p.caption}</p>
                <div className="flex items-center gap-5 mt-3 text-stone-400 text-sm"><span className="flex items-center gap-1.5"><Heart className="w-4 h-4" /> {p.like_count}</span><span className="flex items-center gap-1.5"><MessageCircle className="w-4 h-4" /> {p.comment_count}</span><Share2 className="w-4 h-4 ml-auto" /></div>
              </div>
            </div>
          ))}
          {posts.length === 0 && <p className="text-stone-500 text-center bg-white border border-stone-200 rounded-2xl p-8">Be the first Sakhi to share something 💗</p>}
        </div>
        <div className="text-center mt-6"><Link to="/community" data-testid="sakhi-view-all-feed" className="text-sm font-semibold text-[#6A1B29]">View the full community feed →</Link></div>
      </section>

      {/* PARTICIPATION */}
      <section className="bg-gradient-to-br from-[#6A1B29] to-[#501325] text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 text-center">
          <h2 className="font-display text-3xl sm:text-4xl font-semibold">Your Voice Can Be Part of Suvikka.</h2>
          <div className="flex flex-wrap justify-center gap-2.5 mt-8">
            {PARTICIPATE.map((p, i) => <span key={i} className="bg-white/15 border border-white/20 rounded-full px-4 py-2 text-sm" data-testid={`participate-${i}`}>{p}</span>)}
          </div>
          <Link to="/signup" data-testid="participate-now" className="inline-block mt-8 bg-white text-[#501325] font-semibold px-8 py-3.5 rounded-full hover:scale-105 transition-transform">Participate Now</Link>
        </div>
      </section>

      {/* FEATURED SAKHIS */}
      {featured.length > 0 && (
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <h2 className="font-display text-3xl sm:text-4xl font-semibold text-stone-900 text-center">Meet the Sakhis</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-10">
            {featured.map((s, i) => (
              <div key={s.id} className="bg-white border border-stone-200 rounded-2xl overflow-hidden group" data-testid={`featured-sakhi-${i}`}>
                <div className="aspect-[4/5] overflow-hidden">{s.image && <img src={s.image} alt={s.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />}</div>
                <div className="p-4"><p className="font-display text-lg font-semibold text-stone-900">{s.name}</p><p className="text-xs text-[#6A1B29] font-semibold uppercase tracking-wider">{s.category} · {s.city}</p><p className="text-sm text-stone-500 mt-1.5 line-clamp-2">{s.intro}</p></div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* SAKHI STORIES */}
      {stories.length > 0 && (
        <section className="bg-white border-y border-stone-200">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
            <h2 className="font-display text-3xl sm:text-4xl font-semibold text-stone-900 text-center">Sakhi Stories</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-10">
              {stories.map((s, i) => (
                <div key={s.id} className="rounded-2xl overflow-hidden border border-stone-200 group" data-testid={`story-${i}`}>
                  <div className="aspect-[16/10] overflow-hidden">{s.image && <img src={s.image} alt={s.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />}</div>
                  <div className="p-5"><h3 className="font-display text-lg font-semibold text-stone-900">{s.title}</h3><p className="text-sm text-stone-500 mt-2 line-clamp-3">{s.excerpt}</p><p className="text-xs text-stone-400 mt-3">— {s.author_name}</p></div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CHALLENGES */}
      {challenges.length > 0 && (
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <h2 className="font-display text-3xl sm:text-4xl font-semibold text-stone-900 text-center">What's Happening at Suvikka?</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-10">
            {challenges.map((c, i) => (
              <div key={c.id} className="bg-white border border-stone-200 rounded-2xl overflow-hidden" data-testid={`challenge-${i}`}>
                <div className="relative aspect-[16/10] overflow-hidden">{c.image && <img src={c.image} alt={c.title} loading="lazy" className="w-full h-full object-cover" />}<span className="absolute top-3 left-3 bg-[#6A1B29] text-white text-[10px] font-bold px-2.5 py-1 rounded-full">{c.type} Challenge</span></div>
                <div className="p-5"><h3 className="font-display text-lg font-semibold text-stone-900">{c.title}</h3><p className="text-sm text-stone-500 mt-2">{c.description}</p><div className="flex items-center justify-between mt-4"><span className="text-xs text-stone-400">{c.participants} participating</span><Link to={`/challenge/${c.id}`} data-testid={`challenge-participate-${i}`} className="text-xs font-bold text-white bg-[#6A1B29] px-4 py-2 rounded-full">Participate</Link></div></div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* DISCOVER */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        <h2 className="font-display text-3xl sm:text-4xl font-semibold text-stone-900 text-center">Discover Suvikka</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mt-10">
          {[["People", "/community", Users], ["Stories", "/community", BookOpen], ["Challenges", "/community", Trophy], ["Trending", "/products?mode=trending", Sparkles], ["Products", "/products", Camera]].map(([t, to, Icon], i) => (
            <Link key={i} to={to} data-testid={`discover-${i}`} className="bg-white border border-stone-200 rounded-2xl p-6 text-center hover:border-[#6A1B29] hover:-translate-y-0.5 transition-all">
              <span className="inline-flex p-3 rounded-2xl bg-[#FFF0F4] text-[#6A1B29]"><Icon className="w-6 h-6" /></span>
              <p className="font-medium text-stone-800 mt-3">{t}</p>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
